import type { TagTooltip } from "@/components/Tag/Tag";
import { toFormat } from "@/lib/dateHelpers";
import type { MembershipApplicationSummary, Person } from "@/types";
import type { RequirementRow } from "./pgm-requirements";
import {
  AFFILIATE_APPLICATION_LABEL,
  ASSOCIATE_APPLICATION_LABEL,
  ASSOCIATE_OR_AFFILIATE_APPLICATION_LABEL
} from "./qualifyingLevelRequirements";

const QUALIFYING_APPLICATION_TYPES = ["affiliate", "associate"] as const;
const MEMBER_APPLICATION_TYPE = "member";

function normalizedApplicationType(application: MembershipApplicationSummary): string {
  return application.applicationType?.toLowerCase() ?? "";
}

export function membershipApplicationTypesForRequirementRow(row: RequirementRow): string[] | null {
  if (row.key === "enrollment" || /^pga membership application$/i.test(row.name.trim())) {
    return [MEMBER_APPLICATION_TYPE];
  }

  if (row.key === "associate_application") {
    return ["associate"];
  }

  if (row.name === AFFILIATE_APPLICATION_LABEL || /^affiliate application$/i.test(row.name.trim())) {
    return ["affiliate"];
  }

  if (row.name === ASSOCIATE_APPLICATION_LABEL || /^associate application$/i.test(row.name.trim())) {
    return ["associate"];
  }

  if (
    row.key === "application" ||
    row.name === ASSOCIATE_OR_AFFILIATE_APPLICATION_LABEL ||
    /^(affiliate|associate)?\s*application$/i.test(row.name.trim())
  ) {
    return [...QUALIFYING_APPLICATION_TYPES];
  }

  return null;
}

export function findMembershipApplicationForRequirementRow(
  person: Person,
  row: RequirementRow
): MembershipApplicationSummary | undefined {
  const types = membershipApplicationTypesForRequirementRow(row);
  if (!types) return undefined;

  return (person.membershipApplications ?? []).find((application) =>
    types.includes(normalizedApplicationType(application))
  );
}

function isApprovedApplicationStatus(status?: string | null): boolean {
  const normalized = status?.toLowerCase();
  return normalized === "approved" || normalized === "elected" || normalized === "complete";
}

function isMemberApplicationRequirementRow(row: RequirementRow): boolean {
  return membershipApplicationTypesForRequirementRow(row)?.includes(MEMBER_APPLICATION_TYPE) === true;
}

export function buildMemberApplicationElectionTooltip(
  row: RequirementRow,
  application: MembershipApplicationSummary
): TagTooltip | undefined {
  if (!isMemberApplicationRequirementRow(row)) return undefined;
  if (!isApprovedApplicationStatus(application.status)) return undefined;

  const electionDate = application.electionDate?.electionDate;
  if (!electionDate) return undefined;

  const formattedDate = toFormat(electionDate);
  if (!formattedDate) return undefined;

  return {
    variant: "plain",
    supportingText: `Election: ${formattedDate}`
  };
}

export function withMembershipApplicationLinks(rows: RequirementRow[], person: Person): RequirementRow[] {
  if (!person.membershipApplications?.length) return rows;

  return rows.map((row) => {
    const application = findMembershipApplicationForRequirementRow(person, row);
    if (!application?.id) return row;

    const statusTooltip = row.statusTooltip ?? buildMemberApplicationElectionTooltip(row, application);

    return {
      ...row,
      membershipApplicationId: application.id,
      ...(statusTooltip ? { statusTooltip } : {})
    };
  });
}

export function membershipApplicationDetailsHref(applicationId: string): string {
  return `/people/membership-services/applications/${encodeURIComponent(applicationId)}`;
}

export function membershipApplicationRequirementHref(row: RequirementRow): string | null {
  const applicationId = row.membershipApplicationId;
  if (!applicationId) return null;
  return membershipApplicationDetailsHref(applicationId);
}
